import { useMemo, useState } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { IconButton, Typography } from "@mui/material";
import api, { serializeSearchParams } from "../../utils/api";
import { apiUrls } from "../../utils/urls";
import { Grid } from "@mui/system";
import { Delete } from "@mui/icons-material";
import { useConfirm } from "material-ui-confirm";
import { showSuccessSnackbar } from "../../utils/feedback";

function pad(num) {
  return num.toString().padStart(2, "0");
}

function formatDuration(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export default function RecentTimeEvents({
  clockEventsEndpoint = apiUrls.clock.events(),
}) {
  const confirm = useConfirm();
  const [dataSourceFlag, setDataSourceFlag] = useState(Math.random());
  const dataSource = useMemo(
    () => ({
      // Called by the grid when it needs rows (initial + as you scroll)
      getRows: async (params) => {
        const { start, end } = params; // provided by lazyLoading
        const size = end - start; // number of rows to fetch
        const page = Math.floor(start / size); // API is 1-based

        const resp = await api.get(clockEventsEndpoint, {
          params: serializeSearchParams({
            page,
            size,
            search_params: { clock_out_dt__isnull: false },
            order_by: ["-clock_out_dt"],
            values: ["uuid", "clock_in_dt", "clock_out_dt", "time_worked"],
          }),
        });

        const events = resp.data.data;
        const totalCount = resp.data.total_count; // your API’s total

        return {
          rows: events.map((evt) => ({
            id: evt.uuid,
            clockIn: new Date(evt.clock_in_dt).toLocaleString(),
            clockOut: new Date(evt.clock_out_dt).toLocaleString(),
            duration: formatDuration(evt.time_worked),
          })),
          rowCount: totalCount,
        };
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSourceFlag]
  );

  const handleDelete = (row) => {
    confirm({
      title: "Are you sure?",
      description: `Are you sure you want to delete this clock event with a duration of ${row.duration}? This action is irreversible.`,
    })
      .then(async () => {
        const response = await api.delete(apiUrls.clock.events(row.id));
        if (response?.data.success) {
          showSuccessSnackbar("Clock event deleted!");
          setDataSourceFlag(Math.random());
        }
      })
      .catch(() => {});
  };

  const column = {
    field: "list",
    headerName: "Recent Events",
    flex: 1,
    renderCell: (params) => (
      <Grid
        container
        alignItems={"center"}
        justifyContent={"space-between"}
        key={params.row.uuid}
        sx={{ pb: 1 }}
      >
        <Grid size={10} sx={{ textWrap: "wrap" }}>
          <Typography variant="body1">In: {params.row.clockIn}</Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontSize: "small" }}
          >
            Out: {params.row.clockOut} — Duration: {params.row.duration}
          </Typography>
        </Grid>
        <Grid size={2} sx={{ pl: 2 }}>
          <IconButton onClick={() => handleDelete(params.row)}>
            <Delete />
          </IconButton>
        </Grid>
      </Grid>
    ),
  };

  return (
    <DataGridPro
      disableColumnMenu
      hideFooterSelectedRowCount
      hideFooter
      listView
      columns={[column]}
      listViewColumn={column}
      dataSource={dataSource}
      lazyLoading
      paginationModel={{ page: 0, pageSize: 10 }}
      onDataSourceError={(error) => console.log(error)}
      rowHeight={80}
    />
  );
}
